/**
 * React Query hooks for the AI Gateway Developers panel (Sprint 288 Stream E).
 *
 * Backend: the dedicated ``ai-gateway`` service control API
 * (``/apigw/v1/applications``, ``/apigw/v1/keys``), exposed to Studio under
 * ``/api/v1/gateway-admin`` (dev proxy +
 * ingress; the deployed rule ships with the service manifests — AIGW-10). The
 * tenant is derived server-side from the session, so paths carry no tenant id.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import type {
  ApplicationCreatePayload,
  ApplicationList,
  ApplicationUpdatePayload,
  GatewayApplication,
  GatewayConnectConfig,
  GatewayKey,
  GatewayKeyCreated,
  KeyCreatePayload,
  KeyList,
} from '../types';

const BASE = '/api/v1/gateway-admin';

const keys = {
  applications: () => ['gateway', 'applications'] as const,
  keys: (applicationId: string | null) => ['gateway', 'keys', applicationId] as const,
  connect: () => ['gateway', 'connect'] as const,
};

export function useGatewayApplications() {
  return useQuery({
    queryKey: keys.applications(),
    queryFn: async () => {
      const { data } = await apiClient.get<ApplicationList>(`${BASE}/apigw/v1/applications`);
      return data;
    },
    staleTime: 30 * 1000,
  });
}

export function useGatewayConnectConfig() {
  return useQuery({
    queryKey: keys.connect(),
    queryFn: async () => {
      const { data } = await apiClient.get<GatewayConnectConfig>(
        `${BASE}/apigw/v1/connect/ide`,
      );
      return data;
    },
    staleTime: 60 * 1000,
  });
}

export function useGatewayKeys(applicationId: string | null) {
  return useQuery({
    queryKey: keys.keys(applicationId),
    queryFn: async () => {
      const { data } = await apiClient.get<KeyList>(`${BASE}/apigw/v1/keys`, {
        params: { application_id: applicationId ?? undefined },
      });
      return data;
    },
    enabled: !!applicationId,
    staleTime: 15 * 1000,
  });
}

export function useCreateApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ApplicationCreatePayload) => {
      const { data } = await apiClient.post<GatewayApplication>(
        `${BASE}/apigw/v1/applications`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.applications() });
    },
  });
}

export function useUpdateApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      applicationId,
      payload,
    }: {
      applicationId: string;
      payload: ApplicationUpdatePayload;
    }) => {
      const { data } = await apiClient.patch<GatewayApplication>(
        `${BASE}/apigw/v1/applications/${applicationId}`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.applications() });
    },
  });
}

export function useCreateKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: KeyCreatePayload) => {
      const { data } = await apiClient.post<GatewayKeyCreated>(`${BASE}/apigw/v1/keys`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: keys.keys(variables.application_id) });
    },
  });
}

export function useRotateKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: GatewayKey) => {
      const { data } = await apiClient.post<GatewayKeyCreated>(
        `${BASE}/apigw/v1/keys/${key.id}/rotate`,
      );
      return data;
    },
    onSuccess: (_data, key) => {
      void qc.invalidateQueries({ queryKey: keys.keys(key.application_id) });
    },
  });
}

export function useRevokeKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: GatewayKey) => {
      const { data } = await apiClient.post<GatewayKey>(`${BASE}/apigw/v1/keys/${key.id}/revoke`);
      return data;
    },
    onSuccess: (_data, key) => {
      void qc.invalidateQueries({ queryKey: keys.keys(key.application_id) });
    },
  });
}

export function useSetKeyStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, action }: { key: GatewayKey; action: 'suspend' | 'resume' }) => {
      const { data } = await apiClient.post<GatewayKey>(
        `${BASE}/apigw/v1/keys/${key.id}/${action}`,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: keys.keys(variables.key.application_id) });
    },
  });
}
